import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  useDisclosure,
  Divider,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  HStack,
  VStack,
  useTheme,
  Box,
  CircularProgress,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { InfiniteData } from '@tanstack/react-query';
import { useGetPracticeProblemsQuery } from 'hooks/queries/problems/useGetPracticeProblemsQuery';
import { useQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';
import React from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import GradientTooltip from 'components/GradientTooltip';
import Icon from 'components/Icon';
import Link from 'components/Link';
import PageLoader from 'components/PageLoader';
import Text from 'components/Text';
import { StatusIconMapper, DifficultyMarker } from 'features/practice-problems/PracticeProblems';
import { User_Practice_Problem_Status_Enum } from 'types/databaseEnums';
import { PracticeProblemPreview } from 'types/edgeFunctions';
import { ERoute, TanstackQueryName } from 'types/frontend';

interface IPracticeProblemsListProps {
  data: PracticeProblemPreview[];
  onClose: () => void;
}

const PracticeProblemsList = ({ data, onClose }: IPracticeProblemsListProps) => {
  const { borderRadius } = useTheme();

  return (
    <>
      <VStack width="100%" spacing={2}>
        {data.map(({ id, status, title, screen_name, difficulty }, index) => {
          return (
            <Link
              to={`${ERoute.PracticeProblems}/${screen_name}`}
              onClick={onClose}
              key={id}
              width="100%"
              _hover={{ backgroundColor: 'whiteAlpha.300', textDecoration: 'none' }}
              borderRadius={borderRadius}
              transition="all 0.2s ease-out"
              p={3}
              backgroundColor="whiteAlpha.200"
            >
              <HStack width="100%" justifyContent="space-between">
                <HStack alignItems="center" spacing={4}>
                  <Box>{StatusIconMapper[status]}</Box>
                  <Text fontSize="small">
                    {index + 1}. {title}
                  </Text>
                </HStack>
                <DifficultyMarker difficulty={difficulty} />
              </HStack>
            </Link>
          );
        })}
      </VStack>
    </>
  );
};

const ProblemsDrawer = () => {
  const { id: userId } = useCurrentUser();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const supabase = useSupabaseTypedClient();

  const divRef = React.useRef<HTMLDivElement | null>(null);

  const { data: countData } = useQueryWithErrorBoundary({
    queryKey: [TanstackQueryName.GetPracticeProblemsCount, userId],
    queryFn: async () => {
      return supabase.from('practice_problem').select('*', { count: 'exact', head: true });
    },
  });

  const { data: completedCountData } = useQueryWithErrorBoundary({
    queryKey: [TanstackQueryName.GetPracticeProblemsCompletedCount, userId],
    queryFn: async () => {
      return supabase
        .from('user_practice_problem')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', User_Practice_Problem_Status_Enum.Completed);
    },
  });

  const { data, isFetching, ref } = useGetPracticeProblemsQuery(0, false);

  const practiceProblemsData = (data as unknown as InfiniteData<PracticeProblemPreview[]>) || [];
  const practiceProblems =
    practiceProblemsData.pages.reduce(
      (acc, page) => acc.concat(page),
      [] as PracticeProblemPreview[]
    ) || [];

  const progress =
    completedCountData?.count === 0
      ? 0
      : Math.floor(((completedCountData?.count || 0) / (countData?.count || 100)) * 100);

  const renderPracticeProblems = () => {
    const isFirstPage = practiceProblemsData.pages.length === 0;

    if (isFirstPage && isFetching) {
      return <PageLoader shift />;
    }

    if (isOpen) {
      return (
        <>
          <PracticeProblemsList data={practiceProblems} onClose={onClose} />
          <div ref={ref} />
          <Center width="100%" mt={10}>
            {isFetching && !isFirstPage && <Spinner size="lg" />}
          </Center>
        </>
      );
    }
  };

  return (
    <>
      <div ref={divRef}>
        <Button
          scheme="ghost"
          onClick={onOpen}
          leftIcon={<Icon type="DrawerOpen" size={16} />}
          size="sm"
        >
          <Text fontSize="small" opacity="default">
            Problems
          </Text>
        </Button>
      </div>
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} finalFocusRef={divRef} size="md">
        <DrawerOverlay />
        <DrawerContent backgroundColor="gray.900">
          <DrawerCloseButton mt={1} />
          <DrawerHeader pb={3}>
            <HStack width="100%" justifyContent="space-between">
              <Link
                to={ERoute.PracticeProblems}
                onClick={onClose}
                color="white"
                _hover={{ textDecoration: 'none' }}
              >
                <HStack spacing={1}>
                  <Text fontSize="large" fontWeight="semibold">
                    Problems
                  </Text>
                  <ChevronRightIcon />
                </HStack>
              </Link>
              <GradientTooltip
                label={`${completedCountData?.count || 0}/${countData?.count || 100} completed`}
              >
                <CircularProgress
                  value={progress}
                  color="green.400"
                  thickness="6px"
                  size={4}
                  mr={8}
                  mb={1}
                />
              </GradientTooltip>
            </HStack>
          </DrawerHeader>
          <Divider />
          <DrawerBody p={5}>{renderPracticeProblems()}</DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default ProblemsDrawer;
